import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CardService } from '../services/cardService';
import { QuestionService } from '../services/questionService';
import { RewardsV1Service } from '../services/rewardsV1Service';
import type { QuestionRecord } from '../types/question';
import type { Card } from '../types/card';

function extractQuestionIdsFromReward(reward: any): string[] {
  const raw = reward?.questions ?? reward?.question_refs ?? reward?.question_ids ?? reward?.questionIds;
  if (!Array.isArray(raw)) return [];

  const ids = raw
    .map((v: any) => {
      if (typeof v === 'string') return v;
      if (typeof v === 'number' && Number.isFinite(v)) return String(v);
      if (v && typeof v === 'object') {
        // handles { _id }, { id }, { $oid: '...' }
        const id = v._id ?? v.id ?? v.$oid;
        if (typeof id === 'string') return id;
        if (typeof id === 'number' && Number.isFinite(id)) return String(id);
      }
      return '';
    })
    .map((s: string) => s.trim())
    .filter(Boolean);

  return Array.from(new Set(ids));
}

const AllQuestions: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = user?.role === 'admin';

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [questions, setQuestions] = useState<QuestionRecord[]>([]);

  const [cardInput, setCardInput] = useState<string>('');
  const [cardDropdownOpen, setCardDropdownOpen] = useState<boolean>(false);
  const [cardLoading, setCardLoading] = useState<boolean>(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardOptions, setCardOptions] = useState<Card[]>([]);
  const [selectedCard, setSelectedCard] = useState<Card | null>(null);

  const [rewardCounts, setRewardCounts] = useState<Record<string, number>>({});
  const [rewardCountsLoading, setRewardCountsLoading] = useState<boolean>(false);

  const [questionRefCounts, setQuestionRefCounts] = useState<Record<string, number>>({});
  const [questionRefCountsLoading, setQuestionRefCountsLoading] = useState<boolean>(false);

  const cardIdParam = (searchParams.get('cardid') || '').trim();
  const legacyCardLabelParam = (searchParams.get('card') || '').trim();

  useEffect(() => {
    document.title = 'All Questions';
  }, []);

  useEffect(() => {
    if (!isAdmin) return;

    // Restore typed value if present and no selected card.
    if (!cardIdParam && legacyCardLabelParam) {
      setCardInput((prev) => (prev === legacyCardLabelParam ? prev : legacyCardLabelParam));
    }

    if (!cardIdParam) {
      setSelectedCard(null);
      return;
    }

    let isMounted = true;
    (async () => {
      try {
        const res = await CardService.getCardById(cardIdParam);
        if (!isMounted) return;
        setSelectedCard(res.data);
        setCardInput(`${res.data.bankName} — ${res.data.name}`);
      } catch {
        if (!isMounted) return;
        setSelectedCard(null);
        setCardInput(cardIdParam);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [cardIdParam, isAdmin, legacyCardLabelParam]);

  useEffect(() => {
    if (!isAdmin) return;

    let isMounted = true;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await QuestionService.listActiveQuestions();
        if (!isMounted) return;
        setQuestions(res.data || []);
      } catch (e) {
        if (!isMounted) return;
        setError(e instanceof Error ? e.message : 'Failed to load questions');
      } finally {
        if (!isMounted) return;
        setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin || !cardDropdownOpen) return;

    let isMounted = true;
    const handle = setTimeout(async () => {
      try {
        setCardLoading(true);
        setCardError(null);

        const search = cardInput.trim();
        const res = await CardService.getCards({
          page: 1,
          limit: 20,
          ...(search ? { search } : {}),
        });

        if (!isMounted) return;
        setCardOptions(res.data || []);
      } catch (e) {
        if (!isMounted) return;
        setCardError(e instanceof Error ? e.message : 'Failed to load cards');
        setCardOptions([]);
      } finally {
        if (!isMounted) return;
        setCardLoading(false);
      }
    }, 300);

    return () => {
      isMounted = false;
      clearTimeout(handle);
    };
  }, [cardDropdownOpen, cardInput, isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    if (!selectedCard?._id) {
      setRewardCounts({});
      setRewardCountsLoading(false);
      return;
    }
    if (questions.length === 0) {
      setRewardCounts({});
      setRewardCountsLoading(false);
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        setRewardCountsLoading(true);
        setRewardCounts({});

        const nextCounts: Record<string, number> = {};

        const isActiveReward = (r: any) => String(r?.status || '').toLowerCase() !== 'inactive';

        const fetchActiveCountForQuery = async (cardId: string, query: string): Promise<number> => {
          const limit = 200;
          let page = 1;
          let pages = 1;
          let activeCount = 0;

          do {
            const res = await RewardsV1Service.list({ q: query, cardId, page, limit });
            const data = (res as any)?.data || [];
            for (const r of data) {
              if (isActiveReward(r)) activeCount += 1;
            }
            pages = Number((res as any)?.pages) || 1;
            page += 1;
          } while (!cancelled && page <= pages);

          return activeCount;
        };

        // Concurrency-limited batch: 5 at a time
        const concurrency = 5;
        let idx = 0;

        const cache = new Map<string, number>();

        const worker = async () => {
          while (!cancelled) {
            const current = idx;
            idx += 1;
            const q = questions[current];
            if (!q) return;

            const query = (q.search || q.name || q.question || '').trim();
            if (!query) {
              nextCounts[q._id] = 0;
              continue;
            }

            try {
              const cacheKey = `${selectedCard._id}::${query.toLowerCase()}`;
              if (cache.has(cacheKey)) {
                nextCounts[q._id] = cache.get(cacheKey) || 0;
              } else {
                const activeCount = await fetchActiveCountForQuery(selectedCard._id, query);
                cache.set(cacheKey, activeCount);
                nextCounts[q._id] = activeCount;
              }
            } catch {
              nextCounts[q._id] = 0;
            }

            if (!cancelled) {
              // progressive rendering
              setRewardCounts((prev) => ({ ...prev, [q._id]: nextCounts[q._id] }));
            }
          }
        };

        await Promise.all(Array.from({ length: Math.min(concurrency, questions.length) }, () => worker()));
      } finally {
        if (!cancelled) setRewardCountsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, questions, selectedCard?._id]);

  useEffect(() => {
    if (!isAdmin) return;
    if (!selectedCard?._id) {
      setQuestionRefCounts({});
      setQuestionRefCountsLoading(false);
      return;
    }

    let cancelled = false;
    const run = async () => {
      try {
        setQuestionRefCountsLoading(true);
        setQuestionRefCounts({});

        const isActiveReward = (r: any) => String(r?.status || '').toLowerCase() !== 'inactive';

        const allRewards: any[] = [];
        const limit = 200;
        let page = 1;
        let pages = 1;

        // Fetch all rewards for the selected cardId, then count locally.
        do {
          const res = await RewardsV1Service.list({ cardId: selectedCard._id, page, limit });
          allRewards.push(...((res as any)?.data || []));
          pages = Number((res as any)?.pages) || 1;
          page += 1;
        } while (!cancelled && page <= pages);

        if (cancelled) return;

        const next: Record<string, number> = {};
        for (const r of allRewards) {
          if (!isActiveReward(r)) continue;
          const ids = extractQuestionIdsFromReward(r);
          for (const id of ids) {
            next[id] = (next[id] || 0) + 1;
          }
        }

        if (!cancelled) setQuestionRefCounts(next);
      } catch {
        if (!cancelled) setQuestionRefCounts({});
      } finally {
        if (!cancelled) setQuestionRefCountsLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [isAdmin, selectedCard?._id]);

  const stats = useMemo(() => {
    const uniqueGroups = new Set<string>();
    const uniqueCategories = new Set<string>();
    for (const q of questions) {
      if (q.group) uniqueGroups.add(q.group);
      if (q.category) uniqueCategories.add(q.category);
    }
    return {
      total: questions.length,
      groups: uniqueGroups.size,
      categories: uniqueCategories.size,
    };
  }, [questions]);

  if (!isAdmin) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto w-24 h-24 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-12 h-12 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Denied</h2>
        <p className="text-gray-500 mb-4">You need admin privileges to access Questions.</p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (loading && questions.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <p className="mt-2 text-gray-500">Loading questions...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">All Questions</h1>
          <p className="mt-1 text-sm text-gray-500">Active questions used for expenses</p>
        </div>

        <button
          onClick={() => navigate('/expenses')}
          className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Back
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-md p-4 text-sm">{error}</div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Total</div>
            <div className="text-sm font-medium text-gray-900">{stats.total}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Groups</div>
            <div className="text-sm font-medium text-gray-900">{stats.groups}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Categories</div>
            <div className="text-sm font-medium text-gray-900">{stats.categories}</div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="relative flex-1 min-w-[240px]">
            <label className="block text-xs font-medium text-gray-700">Credit card (search & select)</label>
            <input
              value={cardInput}
              onChange={(e) => {
                setSelectedCard(null);
                setCardInput(e.target.value);
                setCardDropdownOpen(true);

                const params = new URLSearchParams(searchParams);
                params.delete('cardid');
                const v = e.target.value.trim();
                if (v) params.set('card', v);
                else params.delete('card');
                setSearchParams(params);
              }}
              onFocus={() => setCardDropdownOpen(true)}
              onBlur={() => {
                // allow click selection from dropdown
                setTimeout(() => setCardDropdownOpen(false), 150);
              }}
              placeholder="Type bank or card name (e.g., hdfc)"
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />

            {cardDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-sm overflow-hidden">
                {cardLoading && <div className="px-3 py-2 text-sm text-gray-500">Loading…</div>}

                {cardError && !cardLoading && <div className="px-3 py-2 text-sm text-red-700">{cardError}</div>}

                {!cardLoading && !cardError && cardOptions.length === 0 && (
                  <div className="px-3 py-2 text-sm text-gray-500">No cards found.</div>
                )}

                {!cardLoading && !cardError && cardOptions.length > 0 && (
                  <div className="max-h-64 overflow-auto">
                    {cardOptions.map((c) => (
                      <button
                        key={c._id}
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setSelectedCard(c);
                          setCardInput(`${c.bankName} — ${c.name}`);
                          setCardDropdownOpen(false);

                          // Persist selection in URL so Back navigation restores it.
                          const params = new URLSearchParams(searchParams);
                          params.set('cardid', c._id);
                          params.delete('card');
                          setSearchParams(params);
                        }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${selectedCard?._id === c._id ? 'bg-gray-50' : ''}`}
                      >
                        <div className="text-gray-900">{c.bankName} — {c.name}</div>
                        <div className="text-xs text-gray-500 break-all">{c._id}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selectedCard && (
              <div className="mt-2 text-xs text-gray-500">Selected: {selectedCard.bankName} — {selectedCard.name}</div>
            )}
          </div>

          <div className="min-w-[160px]">
            <div className="text-xs font-medium text-gray-700">Reward counts</div>
            <div className="mt-1 text-sm text-gray-900">
              {selectedCard?._id ? (
                rewardCountsLoading ? (
                  <span className="text-gray-500">Calculating…</span>
                ) : (
                  <span className="text-gray-700">Ready</span>
                )
              ) : (
                <span className="text-gray-500">Select a card</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Questions</h2>
          <p className="mt-1 text-xs text-gray-500">Sorted by group, category, frequency</p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 table-fixed">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 w-80 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                <th className="px-4 py-3 w-56 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-4 py-3 w-48 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Search</th>
                <th className="px-4 py-3 w-24 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rewards</th>
                <th className="px-4 py-3 w-24 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Question</th>
                <th className="px-4 py-3 w-24 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prompt</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {questions.map((q) => (
                <tr key={q._id} className="hover:bg-gray-50">
                  <td className="px-4 py-4 align-top">
                    <div className="text-xs text-gray-500">
                      <div>
                        <span className="font-medium text-gray-700">Key:</span> <span className="break-all">{q.questionId || '—'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">ID:</span> <span className="break-all">{q._id || '—'}</span>
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Group:</span> {q.group || '—'}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Category:</span> {q.category || '—'}
                      </div>
                      <div>
                        <span className="font-medium text-gray-700">Frequency:</span> {q.frequency || '—'}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 break-words">{q.name || '—'}</div>
                    {q.description && <div className="text-sm text-gray-500">{q.description}</div>}
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 break-words">{q.search || '—'}</div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    {selectedCard?._id ? (
                      rewardCounts[q._id] != null ? (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800">
                          {rewardCounts[q._id]}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">…</span>
                      )
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>

                  <td className="px-4 py-4 whitespace-nowrap">
                    {selectedCard?._id ? (
                      questionRefCounts[q._id] != null ? (
                        <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800">
                          {questionRefCounts[q._id]}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">…</span>
                      )
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                    {selectedCard?._id && questionRefCountsLoading ? (
                      <span className="sr-only">Loading question counts</span>
                    ) : null}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        q.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {q.isActive ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    {(() => {
                      const query = (q.search || q.name || q.question || '').trim();
                      if (!query) {
                        return <div className="text-sm text-gray-900 break-words">{q.question || '—'}</div>;
                      }

                      const onClick = () => {
                        const params = new URLSearchParams();
                        params.set('q', query);
                        if (selectedCard?._id) params.set('cardid', selectedCard._id);
                        navigate(`/expenses/rewards?${params.toString()}`);
                      };

                      return (
                        <button
                          type="button"
                          onClick={onClick}
                          className="text-left text-sm text-blue-600 hover:text-blue-800 hover:underline break-words"
                          title="Search reward options"
                        >
                          {q.question || query}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              ))}

              {!loading && questions.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">
                    No questions found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AllQuestions;
